/**
 * Google reCAPTCHA v3 client helper (abuse protection Phase 3).
 *
 * The site key comes from GET /api/config/public (recaptcha_site_key). When it
 * is empty, captcha is unconfigured and every helper here no-ops, so forms work
 * exactly as before.
 *
 * Usage:
 *   loadRecaptcha(siteKey);                  // call once when the key is known
 *   const token = await getCaptchaToken(siteKey, "login");  // on form submit
 *   // include token as body.captcha_token (null when unconfigured)
 */

let _scriptPromise = null;

/**
 * Inject the reCAPTCHA v3 script once. Resolves when grecaptcha is ready.
 * No-ops (resolves immediately) when no site key is configured.
 */
export function loadRecaptcha(siteKey) {
  if (!siteKey) return Promise.resolve();
  if (_scriptPromise) return _scriptPromise;

  _scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-recaptcha="v3"]');
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.setAttribute("data-recaptcha", "v3");
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load reCAPTCHA"));
    document.head.appendChild(script);
  });
  return _scriptPromise;
}

/**
 * Execute reCAPTCHA v3 for the given action and return a token string.
 * Returns null when captcha is unconfigured or unavailable — callers should
 * send the body without a token in that case (backend treats null as no-op when
 * its own secret is also unset, and rejects when enforcement is active).
 */
export async function getCaptchaToken(siteKey, action) {
  if (!siteKey) return null;
  try {
    await loadRecaptcha(siteKey);
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha || !grecaptcha.execute) return null;
    await new Promise((resolve) => grecaptcha.ready(resolve));
    return await grecaptcha.execute(siteKey, { action });
  } catch {
    // Token fetch failed — return null. If the backend is enforcing it will 400;
    // if it is not configured the request still succeeds.
    return null;
  }
}
